import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as Console from 'fp-ts/Console';
import { constVoid, pipe } from 'fp-ts/function';
import * as IO from 'fp-ts/IO';
import * as T from 'fp-ts/Task';
import * as TE from 'fp-ts/TaskEither';
import { useEffect, useState } from 'react';
import { activateLens, cancelLens, clearLens, getCurrentLens, loadLens, refreshLens, subscribeToLens } from './api';
import { ActiveErrorLens, ActiveLens, CancelErrorLens, ClearedLens, ClearErrorLens, CreateErrorLens, LoadedLens, LoadingLens, RefreshErrorLens, SuccessStateLens } from './codecs';
import { LensStates } from './lens-states';

var goToRootWindowLocation = function goToRootWindowLocation() {
  return window.location.href = '/';
};

export var useLenses = function useLenses(permissions, profileId) {
  var _useState = useState(null),
      lens = _useState[0],
      setLens = _useState[1];

  var _useState2 = useState(false),
      lensActionPending = _useState2[0],
      setLensActionPending = _useState2[1];

  var _useState3 = useState(null),
      group = _useState3[0],
      setGroup = _useState3[1];

  var _useState4 = useState(null),
      individual = _useState4[0],
      setIndividual = _useState4[1];

  var defaultLensValues = {
    'impersonator-uuid': '',
    'clone-uuid': null,
    'loaded-at': null,
    'group-name': group ? group.name : null,
    'loaded-at-with-timezone': null,
    'impersonator-user': {
      'is-super-admin': false
    }
  };

  var handleRecievingLens = function handleRecievingLens(lens) {
    setLens(lens);

    if (SuccessStateLens.is(lens) && !ClearedLens.is(lens)) {
      if (lens['group-name']) setGroup({
        uuid: '0',
        name: lens['group-name']
      });
      setIndividual({
        uuid: lens['impersonated-user'].uuid,
        name: lens['impersonated-user'].name,
        email: 'lens@placeholder.com',
        image: lens['impersonated-user']['profile-image'],
        title: null,
        company: null
      });
    }
  };

  useEffect(function () {
    if (permissions && permissions.includes('view_lens') && profileId) {
      void subscribeToLens({
        profileId: profileId,
        onLensReceived: handleRecievingLens,
        lensNotificationHandler: pipe(getCurrentLens(handleRecievingLens), TE.getOrElseW(function (err) {
          return T.fromIO(Console.error('Could not retrieve lens on load: ' + JSON.stringify(err)));
        }), IO.apSecond(constVoid))
      })();
    }
  }, [permissions, profileId]);

  var load = function load() {
    if (individual && (ClearedLens.is(lens) || CreateErrorLens.is(lens))) {
      var defaultLens = _objectSpread(_objectSpread({}, defaultLensValues), {}, {
        'impersonated-user': _objectSpread(_objectSpread({}, individual), {}, {
          'profile-image': individual.image
        })
      });

      setLens(_objectSpread({
        state: LensStates.Loading
      }, defaultLens));
      loadLens(individual.uuid, group === null || group === void 0 ? void 0 : group.uuid)()["catch"](function () {
        return setLens(_objectSpread({
          state: LensStates.CreateError
        }, defaultLens));
      });
    }
  };

  var cancel = function cancel() {
    if (LoadingLens.is(lens) || CancelErrorLens.is(lens)) {
      setLensActionPending(true);
      cancelLens().then(function () {
        setLens({
          state: LensStates.Cleared
        });
        setLensActionPending(false);
      })["catch"](function () {
        setLens(_objectSpread(_objectSpread({}, lens), {}, {
          state: LensStates.CancelError
        }));
        setLensActionPending(false);
      });
    }
  };

  var clear = function clear() {
    if (LoadedLens.is(lens) || ActiveLens.is(lens) || ClearErrorLens.is(lens)) {
      setLensActionPending(true);
      clearLens().then(function () {
        return goToRootWindowLocation();
      })["catch"](function () {
        setLensActionPending(false);
        setLens(_objectSpread(_objectSpread({}, lens), {}, {
          state: LensStates.ClearError
        }));
      });
    }
  };

  var activate = function activate() {
    if (LoadedLens.is(lens) || ActiveErrorLens.is(lens)) {
      activateLens().then(function () {
        return goToRootWindowLocation();
      })["catch"](function () {
        return setLens(_objectSpread(_objectSpread({}, lens), {}, {
          state: LensStates.ActiveError
        }));
      });
    }
  };

  var refresh = function refresh() {
    if (ActiveLens.is(lens) || RefreshErrorLens.is(lens)) {
      refreshLens().then(function () {
        return goToRootWindowLocation();
      })["catch"](function () {
        return setLens(_objectSpread(_objectSpread({}, lens), {}, {
          state: LensStates.RefreshError
        }));
      });
    }
  };

  return {
    current: lens,
    lensActionPending: lensActionPending,
    actions: {
      loadLens: load,
      cancelLens: cancel,
      clearLens: clear,
      activateLens: activate,
      refreshLens: refresh
    },
    group: group,
    setGroup: setGroup,
    individual: individual,
    setIndividual: setIndividual
  };
};